import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DownArrowIcon, UpArrowIcon } from 'assests/GmsIcons';

type DropdownOption = {
    label: string;
    value: string;
    onClick: () => void;
};

type DropdownProps = {
    id: string;
    label: string;
    options: DropdownOption[];
    className?: string;
};

export const Dropdown = ({
    id,
    label,
    options,
    className = ''
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };

    const handleOptionClick = (option: DropdownOption) => {
        option.onClick();
        setIsOpen(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
        } else if (event.key === 'Escape' && isOpen) {
            setIsOpen(false);
        }
    };

    return (
        <div
            ref={dropdownRef}
            className={`tw-relative tw-inline-block ${className}`}
            data-testid={id}
        >
            <button
                type="button"
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-border tw-px-3 tw-py-2 tw-text-base tw-text-black tw-transition-colors focus:tw-outline-none focus:tw-ring-[rgb(208,208,208)] ${isOpen
                    ? 'tw-bg-[rgb(245,245,245)] tw-border-[rgb(208,208,208)]'
                    : 'tw-bg-white tw-border-[rgb(208,208,208)] hover:tw-bg-[rgb(245,245,245)]'
                    }`}
                aria-haspopup="true"
                aria-expanded={isOpen}
                data-testid={`${id}-trigger`}
            >
                <span className="tw-font-medium tw-text-sm">{label}</span>
                <div className="tw-flex tw-items-center">
                    {isOpen ? <UpArrowIcon color='#000000' /> : <DownArrowIcon color='#000000' />}
                </div>
            </button>

            {isOpen && (
                <ul
                    className="bg-white dropdown-menu tw-text-sm show tw-absolute tw-right-0 tw-top-full tw-z-50 tw-mt-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby={`${id}-trigger`}
                    data-testid={`${id}-menu`}
                >
                    {options.map((option, index) => (
                        <li key={option.value}>
                            <Link
                                className="dropdown-item"
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleOptionClick(option);
                                }}
                                role="menuitem"
                                data-testid={`${id}-option-${index}`}
                            >
                                {option.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};